import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Pencil, Trash2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Card, SectionTitle } from "@/components/Kpi";
import { TipologieUtenza } from "@/components/TipologieUtenza";
import { inputCls, useFiltersMeta } from "@/components/Filters";
import { RegolePassword, REGOLE_PASSWORD } from "@/pages/CambiaPassword";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_PAGINE = ["home", "commerciali", "giornaliero", "convenzionati", "mappa",
  "classifiche", "pratiche", "performance"];
const VUOTO = { email: "", password: "", name: "", role: "COMMERCIALE",
  pagine: DEFAULT_PAGINE, commerciali: [], funzioni: [], canali: ["lungo"], colonna_calendario: "", pagina_iniziale_libera: false, agenzie_report: [] };
const COLONNE_CALENDARIO = [["", "Nessuna (sola lettura)"], ["gianluca", "GIANLUCA"], ["erika", "ERIKA"], ["pamela", "PAMELA"]];
const RUOLI = ["COMMERCIALE", "HUB", "VISITATORE", "MASTER"];

/** Agenzie visibili nel Report agenzia (vuoto = tutte, DBE compreso). */
function AgenzieReport({ valori, onChange, agenzie, testId }) {
  const toggle = (a) => onChange(valori.includes(a) ? valori.filter((x) => x !== a) : [...valori, a]);
  return (
    <div data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Report agenzia · agenzie visibili</div>
      <div className="text-xs text-slate-500 mb-2">{valori.length ? `Solo: ${valori.join(", ")}` : "Nessuna selezione = tutte le agenzie e il totale DBE"}</div>
      <div className="flex flex-wrap gap-1.5">
        {["DBE", ...agenzie].map((a) => (
          <button type="button" key={a} onClick={() => toggle(a)} data-testid={`${testId}-${a.toLowerCase().replace(/\s+/g, "-")}`}
            className={`px-2 py-0.5 rounded-full text-xs border ${valori.includes(a) ? "bg-[#0E7490] text-white border-[#0E7490]" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}>
            {a}
          </button>
        ))}
      </div>
    </div>
  );
}
const lbl = "block text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-2";

function ListaSpunte({ voci, selezionati, onToggle, disabled, prefix }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      {voci.map((v) => (
        <label key={v.key} className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" data-testid={`${prefix}-${v.key}`} disabled={disabled}
            checked={disabled || selezionati.includes(v.key)} onChange={() => onToggle(v.key)} />
          <span className="truncate">{v.label}</span>
        </label>
      ))}
    </div>
  );
}

function BloccoCanale({ canale, pagine, funzioni, stato, setStato, prefix, master, extra }) {
  const sempre = canale.key === "globale";
  const attivo = sempre || master || (stato.canali || []).includes(canale.key);
  const pag = pagine.filter((p) => (p.canale || "lungo") === canale.key);
  const fun = funzioni.filter((f) => (f.canale || "lungo") === canale.key);
  const toggleCanale = () => setStato((s) => ({
    ...s,
    canali: (s.canali || []).includes(canale.key)
      ? (s.canali || []).filter((k) => k !== canale.key)
      : [...(s.canali || []), canale.key],
  }));
  return (
    <div className={`rounded-lg border ${attivo ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50"}`}
      data-testid={`${prefix}-blocco-${canale.key}`}>
      <label className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 cursor-pointer">
        <input type="checkbox" data-testid={`${prefix}-canale-${canale.key}`}
          disabled={sempre} checked={attivo} onChange={toggleCanale} />
        <span className="text-sm font-semibold text-slate-900">{canale.label}</span>
        <span className="ml-auto text-[11px] uppercase tracking-[0.14em] text-slate-400">
          {attivo ? `${pag.length} pagine · ${fun.length} autorizzazioni` : "canale inibito"}
        </span>
      </label>
      <div className={`px-4 py-4 space-y-5 transition-opacity duration-200 ${
        attivo ? "" : "opacity-40 pointer-events-none select-none"}`}
        data-testid={`${prefix}-contenuto-${canale.key}`}>
        <div>
          <div className={lbl}>Pagine visibili {master && "(il Master vede sempre tutto il canale attivo)"}</div>
          <ListaSpunte voci={pag} selezionati={stato.pagine} prefix={`${prefix}-perm`}
            disabled={master || !attivo} onToggle={(k) => setStato((s) => ({
              ...s, pagine: s.pagine.includes(k) ? s.pagine.filter((x) => x !== k) : [...s.pagine, k] }))} />
        </div>
        {fun.length ? (
          <div>
            <div className={lbl}>Autorizzazioni di questo canale</div>
            <ListaSpunte voci={fun} selezionati={stato.funzioni} prefix={`${prefix}-fun`}
              disabled={master || !attivo} onToggle={(k) => setStato((s) => ({
                ...s, funzioni: s.funzioni.includes(k) ? s.funzioni.filter((x) => x !== k) : [...s.funzioni, k] }))} />
          </div>
        ) : null}
        {attivo ? extra : null}
      </div>
    </div>
  );
}

export default function Utenti() {
  const { isMaster } = useAuth();
  const ruoliScelta = (lista) => isMaster ? lista : lista.filter((r) => r !== "MASTER" && r !== "ADMIN");
  const meta = useFiltersMeta();
  const [rows, setRows] = useState([]);
  const [pagine, setPagine] = useState([]);
  const [canali, setCanali] = useState([]);
  const [funzioni, setFunzioni] = useState([]);
  const [ruoli, setRuoli] = useState({ base: RUOLI, ruoli: [], tutti: RUOLI });
  const [form, setForm] = useState(VUOTO);
  const [edit, setEdit] = useState(null);
  const [pwVisibile, setPwVisibile] = useState(true);

  const [agenzieCorto, setAgenzieCorto] = useState([]);
  const load = () => api.get("/users").then((r) => setRows(r.data.rows)).catch(() => setRows([]));
  useEffect(() => {
    load();
    api.get("/auth/pagine").then((r) => {
      setPagine(r.data.pagine || []);
      setCanali([...(r.data.canali || []),
        { key: "globale", label: "Accessi trasversali (validi su entrambi i canali)" }]);
    }).catch(() => { setPagine([]); setCanali([]); });
    api.get("/auth/funzioni").then((r) => setFunzioni(r.data.funzioni || [])).catch(() => setFunzioni([]));
    api.get("/corto/agenzie").then((r) => setAgenzieCorto((r.data.rows || []).map((a) => a.agenzia))).catch(() => setAgenzieCorto([]));
    caricaRuoli();
  }, []);

  const commercialiVoci = (meta?.commerciali || []).map((c) => ({ key: c.slug, label: c.nome }));
  const toggle = (setState, campo) => (key) => setState((f) => ({
    ...f, [campo]: f[campo].includes(key) ? f[campo].filter((k) => k !== key) : [...f[campo], key] }));

  const caricaRuoli = () => api.get("/ruoli")
    .then((r) => setRuoli(r.data)).catch(() => setRuoli({ base: RUOLI, ruoli: [], tutti: RUOLI }));

  const defaultRuolo = (key) => {
    const r = (ruoli.ruoli || []).find((x) => x.key === key);
    return r ? { pagine: r.pagine || [], funzioni: r.funzioni || [] } : {};
  };
  const etichettaFunzione = (k) => (funzioni.find((f) => f.key === k)?.label || k);

  const crea = async (e) => {
    e.preventDefault();
    if (!REGOLE_PASSWORD.every((r) => r.test(form.password))) {
      return toast.error("La password non rispetta i requisiti di sicurezza");
    }
    try {
      await api.post("/users", {
        email: form.email, password: form.password, name: form.name, role: form.role,
        commerciale_slug: form.commerciali[0] || null,
        permissions: { pagine: form.pagine, canali: form.canali, commerciali_visibili: form.commerciali,
          funzioni: form.funzioni, colonna_calendario: form.colonna_calendario || null,
          pagina_iniziale_libera: Boolean(form.pagina_iniziale_libera), agenzie_report: form.agenzie_report },
      });
      toast.success("Utente creato");
      setForm(VUOTO);
      load();
    } catch (e2) { toast.error(apiError(e2)); }
  };

  const salvaModifiche = async () => {
    try {
      await api.put(`/users/${edit.id}`, {
        name: edit.name, role: edit.role,
        commerciale_slug: edit.commerciali[0] || null,
        permissions: { pagine: edit.pagine, canali: edit.canali, commerciali_visibili: edit.commerciali,
          funzioni: edit.funzioni, colonna_calendario: edit.colonna_calendario || null,
          pagina_iniziale_libera: Boolean(edit.pagina_iniziale_libera), agenzie_report: edit.agenzie_report },
        ...(edit.password ? { password: edit.password } : {}),
      });
      toast.success(`Permessi di ${edit.name} aggiornati`);
      setEdit(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const apriModifica = (u) => setEdit({
    id: u.id, name: u.name, email: u.email, role: u.role, password: "",
    pagine: u.permissions?.pagine?.length ? u.permissions.pagine : DEFAULT_PAGINE,
    canali: u.permissions?.canali?.length ? u.permissions.canali : ["lungo"],
    funzioni: u.permissions?.funzioni || [],
    colonna_calendario: u.permissions?.colonna_calendario || "",
    pagina_iniziale_libera: Boolean(u.permissions?.pagina_iniziale_libera),
    agenzie_report: u.permissions?.agenzie_report || [],
    commerciali: u.permissions?.commerciali_visibili?.length
      ? u.permissions.commerciali_visibili
      : (u.commerciale_slug ? [u.commerciale_slug] : []),
  });

  const elimina = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("Utente eliminato"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const nomeComm = (slug) => commercialiVoci.find((c) => c.key === slug)?.label || slug;

  return (
    <div data-testid="utenti-page">
      <SectionTitle sub="I permessi sono validati lato backend su ogni endpoint, non solo nell'interfaccia.">
        Utenti e permessi
      </SectionTitle>

      <TipologieUtenza pagine={pagine} funzioni={funzioni} onChange={caricaRuoli} />

      <Card className="p-6 mb-8">
        <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-4">Nuovo utente</h3>
        <form onSubmit={crea} data-testid="user-form">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <input className={inputCls} placeholder="Nome" required value={form.name} data-testid="user-name"
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input className={inputCls} type="email" placeholder="Email" required value={form.email}
              data-testid="user-email" onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <div>
              <div className="relative">
                <input className={`${inputCls} w-full pr-10`} type={pwVisibile ? "text" : "password"}
                  placeholder="Password iniziale" required autoComplete="off"
                  value={form.password} data-testid="user-password"
                  onChange={(e) => setForm({ ...form, password: e.target.value })} />
                <button type="button" onClick={() => setPwVisibile((v) => !v)} data-testid="user-password-toggle"
                  title={pwVisibile ? "Nascondi password" : "Mostra password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors duration-200">
                  {pwVisibile ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {form.password && <RegolePassword valore={form.password} />}
              <p className="text-[11px] text-slate-400 mt-1">
                {form.role === "VISITATORE"
                  ? "Il visitatore entra direttamente con questa password."
                  : "L'utente dovrà cambiarla al primo accesso."}
              </p>
            </div>
            <select className={inputCls} value={form.role} data-testid="user-role"
              onChange={(e) => setForm({ ...form, role: e.target.value, ...defaultRuolo(e.target.value) })}>
              {ruoliScelta(ruoli.tutti || RUOLI).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <div>
              <div className={lbl}>Colonna calendario corto</div>
              <select className={`${inputCls} w-full`} value={form.colonna_calendario} data-testid="user-colonna-calendario"
                onChange={(e) => setForm({ ...form, colonna_calendario: e.target.value })}>
                {COLONNE_CALENDARIO.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div>
              <div className={lbl}>Pagina iniziale</div>
              <label className="flex items-center gap-2 text-sm cursor-pointer mt-2">
                <input type="checkbox" checked={Boolean(form.pagina_iniziale_libera)} className="accent-sky-600"
                  data-testid="user-pagina-iniziale-libera" onChange={(e) => setForm({ ...form, pagina_iniziale_libera: e.target.checked })} />
                Può scegliere la propria pagina iniziale
              </label>
            </div>
          </div>
          {form.pagine.includes("corto_report_agenzia") && (
            <AgenzieReport valori={form.agenzie_report} agenzie={agenzieCorto} testId="user-agenzie-report"
              onChange={(v) => setForm({ ...form, agenzie_report: v })} />
          )}

          <div className="mt-6 space-y-4">
            <div className={lbl}>Canali, pagine e autorizzazioni</div>
            {canali.map((c) => (
              <BloccoCanale key={c.key} canale={c} pagine={pagine} funzioni={funzioni}
                stato={form} setStato={setForm} prefix="new" master={form.role === "MASTER"}
                extra={c.key === "lungo" ? (
                  <div>
                    <div className={lbl}>Commerciali visibili (puoi selezionarne più di uno)</div>
                    <ListaSpunte voci={commercialiVoci} selezionati={form.commerciali} prefix="comm"
                      disabled={form.role === "MASTER"} onToggle={toggle(setForm, "commerciali")} />
                    <button type="button" data-testid="comm-tutti"
                      onClick={() => setForm({ ...form, commerciali: commercialiVoci.map((x) => x.key) })}
                      className="mt-3 text-xs text-sky-700 hover:text-sky-900 transition-colors duration-200">
                      Seleziona tutti i commerciali
                    </button>
                    <span className="mx-2 text-slate-300">·</span>
                    <button type="button" data-testid="comm-nessuno"
                      onClick={() => setForm({ ...form, commerciali: [] })}
                      className="text-xs text-slate-400 hover:text-slate-700 transition-colors duration-200">
                      Nessuno
                    </button>
                  </div>
                ) : null} />
            ))}
          </div>

          <button type="submit" data-testid="user-save"
            className="mt-6 bg-slate-900 text-white rounded-md px-5 py-2 text-sm font-medium
              hover:bg-slate-800 transition-colors duration-200">
            Crea utente
          </button>
        </form>
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead><tr className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
            <th className="text-left px-6 py-3">Nome</th><th className="text-left px-6 py-3">Email</th>
            <th className="text-left px-6 py-3">Ruolo</th>
            <th className="text-left px-6 py-3">Ultimo accesso</th>
            <th className="text-left px-6 py-3">Commerciali visibili</th>
            <th className="text-left px-6 py-3">Pagine</th>
            <th className="text-left px-6 py-3">Funzioni</th><th className="px-6 py-3" />
          </tr></thead>
          <tbody>
            {rows.map((u) => {
              const comms = u.permissions?.commerciali_visibili?.length
                ? u.permissions.commerciali_visibili
                : (u.commerciale_slug ? [u.commerciale_slug] : []);
              return (
                <tr key={u.id} className="border-t border-slate-100" data-testid={`user-row-${u.email}`}>
                  <td className="px-6 py-3 font-medium">{u.name}</td>
                  <td className="px-6 py-3 text-slate-600">{u.email}</td>
                  <td className="px-6 py-3">{u.role}</td>
                  <td className="px-6 py-3 text-xs whitespace-nowrap" data-testid={`user-ultimo-accesso-${u.id}`}>
                    {u.ultimo_accesso
                      ? <span className="text-slate-600">{new Date(u.ultimo_accesso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}
                          {u.accessi ? <span className="text-slate-400"> · {u.accessi} accessi</span> : null}</span>
                      : <span className="inline-block rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 font-medium">mai entrato</span>}
                  </td>
                  <td className="px-6 py-3 text-xs text-slate-500">
                    {u.role === "MASTER" ? "tutti"
                      : comms.length === commercialiVoci.length && comms.length ? "tutti"
                        : comms.map(nomeComm).join(", ") || "—"}
                  </td>
                  <td className="px-6 py-3 text-xs text-slate-500">
                    {u.role === "MASTER" ? "tutte" : (u.permissions?.pagine || []).length || "default ruolo"}
                  </td>
                  <td className="px-6 py-3 text-xs text-slate-500">
                    {u.role === "MASTER" ? "tutte"
                      : (u.permissions?.funzioni || []).length
                        ? (u.permissions.funzioni || []).map(etichettaFunzione).join(", ")
                        : "nessuna"}
                  </td>
                  <td className="px-6 py-3 text-right whitespace-nowrap">
                    <button onClick={() => apriModifica(u)} data-testid={`user-edit-${u.email}`}
                      className="text-slate-500 hover:text-sky-700 mr-4 transition-colors duration-200"
                      title="Modifica permessi">
                      <Pencil size={15} />
                    </button>
                    {u.role !== "MASTER" && (
                      <button onClick={() => elimina(u.id)} data-testid={`user-delete-${u.email}`}
                        className="text-red-500 hover:text-red-700 transition-colors duration-200" title="Elimina">
                        <Trash2 size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {edit && (
        <div className="fixed inset-0 z-[1200] flex items-start justify-center overflow-y-auto
          bg-slate-900/50 backdrop-blur-sm px-4 py-8" data-testid="user-edit-modal">
          <div className="w-full max-w-3xl rounded-xl bg-white shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200
              bg-gradient-to-r from-sky-50 to-white rounded-t-xl">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-sky-700">Modifica utente</div>
                <h2 className="text-lg font-semibold text-slate-900">{edit.email}</h2>
              </div>
              <button type="button" onClick={() => setEdit(null)} data-testid="user-edit-chiudi"
                className="p-2 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors duration-200">
                <X size={18} />
              </button>
            </div>
            <div className="px-6 py-5 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <div className={lbl}>Nome</div>
                  <input className={`${inputCls} w-full`} value={edit.name} data-testid="edit-name"
                    onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
                </div>
                <div>
                  <div className={lbl}>Ruolo</div>
                  <select className={`${inputCls} w-full`} value={edit.role} data-testid="edit-role"
                    disabled={edit.role === "MASTER"}
                    onChange={(e) => setEdit({ ...edit, role: e.target.value })}>
                    {ruoliScelta(ruoli.tutti || RUOLI).map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <div className={lbl}>Colonna calendario corto</div>
                  <select className={`${inputCls} w-full`} value={edit.colonna_calendario} data-testid="edit-colonna-calendario"
                    onChange={(e) => setEdit({ ...edit, colonna_calendario: e.target.value })}>
                    {COLONNE_CALENDARIO.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <div className={lbl}>Pagina iniziale</div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer mt-2">
                    <input type="checkbox" checked={Boolean(edit.pagina_iniziale_libera)} className="accent-sky-600"
                      data-testid="edit-pagina-iniziale-libera" onChange={(e) => setEdit({ ...edit, pagina_iniziale_libera: e.target.checked })} />
                    Può scegliere la propria pagina iniziale
                  </label>
                </div>
                {edit.pagine.includes("corto_report_agenzia") && (
                  <div className="md:col-span-3">
                    <AgenzieReport valori={edit.agenzie_report} agenzie={agenzieCorto} testId="edit-agenzie-report"
                      onChange={(v) => setEdit({ ...edit, agenzie_report: v })} />
                  </div>
                )}
                <div>
                  <div className={lbl}>Nuova password (opzionale)</div>
                  <div className="relative">
                    <input className={`${inputCls} w-full pr-10`} type={pwVisibile ? "text" : "password"}
                      value={edit.password} data-testid="edit-password" autoComplete="off"
                      placeholder="lascia vuoto per non cambiarla"
                      onChange={(e) => setEdit({ ...edit, password: e.target.value })} />
                    <button type="button" onClick={() => setPwVisibile((v) => !v)} data-testid="edit-password-toggle"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors duration-200">
                      {pwVisibile ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {edit.password && <RegolePassword valore={edit.password} />}
                </div>
              </div>
              <div className="space-y-4">
                <div className={lbl}>Canali, pagine e autorizzazioni</div>
                {canali.map((c) => (
                  <BloccoCanale key={c.key} canale={c} pagine={pagine} funzioni={funzioni}
                    stato={edit} setStato={setEdit} prefix="edit" master={edit.role === "MASTER"}
                    extra={c.key === "lungo" ? (
                      <div>
                        <div className={lbl}>Commerciali visibili</div>
                        <ListaSpunte voci={commercialiVoci} selezionati={edit.commerciali}
                          prefix="edit-comm" disabled={edit.role === "MASTER"}
                          onToggle={toggle(setEdit, "commerciali")} />
                        <button type="button" data-testid="edit-comm-tutti"
                          onClick={() => setEdit({ ...edit, commerciali: commercialiVoci.map((x) => x.key) })}
                          className="mt-3 text-xs text-sky-700 hover:text-sky-900 transition-colors duration-200">
                          Seleziona tutti i commerciali
                        </button>
                        <span className="mx-2 text-slate-300">·</span>
                        <button type="button" data-testid="edit-comm-nessuno"
                          onClick={() => setEdit({ ...edit, commerciali: [] })}
                          className="text-xs text-slate-400 hover:text-slate-700 transition-colors duration-200">
                          Nessuno
                        </button>
                      </div>
                    ) : null} />
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <button type="button" onClick={() => setEdit(null)} data-testid="edit-annulla"
                className="text-sm px-4 py-2 rounded-md border border-slate-200 bg-white text-slate-600
                  hover:border-slate-400 transition-colors duration-200">Annulla</button>
              <button type="button" onClick={salvaModifiche} data-testid="edit-salva"
                disabled={Boolean(edit.password) && !REGOLE_PASSWORD.every((r) => r.test(edit.password))}
                className="text-sm px-5 py-2 rounded-md bg-sky-600 text-white font-semibold
                  hover:bg-sky-700 disabled:opacity-50 transition-colors duration-200">
                Salva modifiche
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
